<template>
  <div
    ref="container"
    class="pdf-preview"
    data-cy="pdf-preview"
  >
    <v-progress-linear
      v-show="loading"
      color="primary"
      indeterminate
      data-cy="pdf-preview-loading"
    />
    <v-card-actions class="justify-center">
      <v-btn
        size="small"
        :disabled="loading || currentPage <= 1"
        data-cy="pdf-preview-previous"
        @click="previousPage"
      >
        Prejšnja stran
      </v-btn>
      <v-btn
        size="small"
        :disabled="loading || currentPage >= numOfPages"
        data-cy="pdf-preview-next"
        @click="nextPage"
      >
        Naslednja stran
      </v-btn>
    </v-card-actions>
    <div class="text-center">
      <span>Stran: <span>{{ currentPage }}</span> / <span>{{ numOfPages }}</span></span>
    </div>
    <canvas
      ref="canvas"
      class="pdf-preview-canvas"
      data-cy="pdf-preview-canvas"
    />
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorkerEntry from 'pdfjs-dist/build/pdf.worker.entry';
import { markRaw } from 'vue';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerEntry;

export default {
  name: "PdfPreview",
  props: {
    documentUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      currentPage: 1,
      numOfPages: null,
      pdfRef: null,
      renderTask: null,
    }
  },
  mounted() {
    this.renderPdf();
  },
  beforeUnmount() {
    if (this.renderTask) {
      this.renderTask.cancel();
    }

    if (this.pdfRef) {
      this.pdfRef.destroy();
    }
  },
  methods: {
    async renderPdf() {
      this.pdfRef = markRaw(await pdfjsLib.getDocument(this.documentUrl).promise);
      this.numOfPages = this.pdfRef.numPages;
      await this.loadPage();
    },
    async previousPage() {
      if (this.currentPage - 1 > 0) {
        this.currentPage--;
      }

      return this.loadPage();
    },
    async nextPage() {
      if (this.currentPage + 1 <= this.numOfPages) {
        this.currentPage++;
      }

      return this.loadPage();
    },
    async loadPage() {
      if (!this.pdfRef) {
        return;
      }

      this.loading = true;
      const page = await this.pdfRef.getPage(this.currentPage);
      const containerWidth = this.$refs.container.clientWidth;
      const widthAtScale1 = 595;
      const scale = containerWidth / widthAtScale1;
      const viewport = page.getViewport({scale: scale});

      // Prepare canvas using PDF page dimensions
      const canvas = this.$refs.canvas;
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      // Render PDF page into canvas context
      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };

      const renderTask = page.render(renderContext);
      this.renderTask = markRaw(renderTask);

      try {
        await renderTask.promise;
      } finally {
        this.renderTask = null;
        this.loading = false;
      }
    },
    downloadPdf() {
      window.open(this.documentUrl, '_blank');
    },
  }
}
</script>

<style scoped>
  .pdf-preview {
    width: 100%;
    overflow: hidden;
  }

  .pdf-preview-canvas {
    display: block;
    max-width: 100%;
    height: auto;
    margin: 0 auto;
  }
</style>
